import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { API_BASE_URL, API_DOMAIN } from "../utils/config";
import { useToast } from "../context/ToastContext";
import { 
  FaPlus, FaEdit, FaTrash, FaGraduationCap, 
  FaUniversity, FaTimes,
  FaBriefcase, FaFilePdf, FaUpload, FaInfoCircle
} from "react-icons/fa";

const CandidateEducation = () => {
  const { showToast } = useToast();

  const [educationList, setEducationList] = useState([]);
  const [isEduModalOpen, setIsEduModalOpen] = useState(false);
  const [eduFormData, setEduFormData] = useState({
    qualification: "", institution: "", startDate: "", endDate: "", passingYear: "", location: "", percentage: ""
  });
  const [editingEduId, setEditingEduId] = useState(null);

  const [experienceList, setExperienceList] = useState([]);
  const [isExpModalOpen, setIsExpModalOpen] = useState(false);
  const [expFormData, setExpFormData] = useState({
    companyName: '', role: '', location: '', salary: '', startDate: '', endDate: '', 
    offerLetter: null, payslip: null, relievingLetter: null, otherDocument: null
  });
  const [editingExpId, setEditingExpId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("candidateToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const getCandidateId = () => localStorage.getItem("candidateId") || null;

  const renderDocumentUrl = (path) => {
    if (!path) return "#";
    if (path.startsWith("http")) return path;
    const normalizedPath = path.replace(/\\/g, '/');
    if (normalizedPath.includes('/uploads/')) {
      const parts = normalizedPath.split('/uploads/');
      return `${API_DOMAIN}/uploads/${parts[1]}`;
    }
    const cleanPath = normalizedPath.startsWith('/') ? normalizedPath.substring(1) : normalizedPath;
    return `${API_DOMAIN}/${cleanPath}`;
  };

  const fetchData = async () => {
    setLoading(true);
    const candidateId = getCandidateId();
    const headers = getAuthHeaders();

    // Education - alag try catch
    try {
      const eduRes = await axios.get(`${API_BASE_URL}/candidate/education`, { headers, params: { candidateId } });
      setEducationList(eduRes.data?.data || eduRes.data || []);
    } catch (err) {
      console.error("Education fetch failed:", err.message);
      setEducationList([]);
    }

    // Experience - alag try catch, education fail hone se affect nahi hoga
    try {
      const expRes = await axios.get(`${API_BASE_URL}/candidate/experience`, { headers, params: { candidateId } });
      setExperienceList(expRes.data?.data || expRes.data || []);
    } catch (err) {
      console.error("Experience fetch failed:", err.message);
      setExperienceList([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEduSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = { ...eduFormData, candidateId: getCandidateId() };
      let res;
      if (editingEduId) {
        res = await axios.put(`${API_BASE_URL}/candidate/education/${editingEduId}`, payload, { headers: getAuthHeaders() });
      } else {
        res = await axios.post(`${API_BASE_URL}/candidate/education`, payload, { headers: getAuthHeaders() });
      }
      if (res.data.success) {
        showToast("Academic record synchronized", "success");
        fetchData();
        setIsEduModalOpen(false);
        setEditingEduId(null);
        setEduFormData({ qualification: "", institution: "", startDate: "", endDate: "", passingYear: "", location: "", percentage: "" });
      }
    } catch (err) {
      showToast("Transmission failed", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEduEdit = (edu) => {
    setEditingEduId(edu._id);
    setEduFormData({
      qualification: edu.qualification || "",
      institution: edu.institution || "",
      startDate: edu.startDate ? new Date(edu.startDate).toISOString().split('T')[0] : "",
      endDate: edu.endDate ? new Date(edu.endDate).toISOString().split('T')[0] : "",
      passingYear: edu.passingYear || "",
      location: edu.location || "",
      percentage: edu.percentage || ""
    });
    setIsEduModalOpen(true);
  };

  const handleEduDelete = async (id) => {
    if (!window.confirm("Purge this academic record?")) return;
    try {
      const res = await axios.delete(`${API_BASE_URL}/candidate/education/${id}`, { headers: getAuthHeaders() });
      if (res.data.success) {
        showToast("Record purged", "success");
        fetchData();
      }
    } catch (err) {
      showToast("Action failed", "error");
    }
  };

  const handleExpSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const data = new FormData();
    Object.keys(expFormData).forEach(key => {
      if (expFormData[key]) data.append(key, expFormData[key]);
    });
    data.append("candidateId", getCandidateId());

    try {
      let res;
      const headers = { 'Content-Type': 'multipart/form-data', ...getAuthHeaders() };
      if (editingExpId) {
        res = await axios.put(`${API_BASE_URL}/candidate/experience/${editingExpId}`, data, { headers });
      } else {
        res = await axios.post(`${API_BASE_URL}/candidate/experience`, data, { headers });
      }
      if (res.data?.success || res.status === 200 || res.status === 201) {
        showToast("Professional record synchronized", "success");
        fetchData();
        setIsExpModalOpen(false);
        setEditingExpId(null);
        setExpFormData({ companyName: '', role: '', location: '', salary: '', startDate: '', endDate: '', offerLetter: null, payslip: null, relievingLetter: null, otherDocument: null });
      }
    } catch (err) {
      showToast("Transmission failed", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExpEdit = (exp) => {
    setEditingExpId(exp._id);
    setExpFormData({
      companyName: exp.companyName || '',
      role: exp.role || '',
      location: exp.location || '',
      salary: exp.salary || '',
      startDate: exp.startDate ? new Date(exp.startDate).toISOString().split('T')[0] : '',
      endDate: exp.endDate ? new Date(exp.endDate).toISOString().split('T')[0] : '',
      offerLetter: null, payslip: null, relievingLetter: null, otherDocument: null
    });
    setIsExpModalOpen(true);
  };

  const handleExpDelete = async (id) => {
    if (!window.confirm("Purge this professional record?")) return;
    try {
      const res = await axios.delete(`${API_BASE_URL}/candidate/experience/${id}`, { headers: getAuthHeaders() });
      if (res.data.success) {
        showToast("Record purged", "success");
        fetchData();
      }
    } catch (err) {
      showToast("Action failed", "error");
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'PRESENT';

  return (
    <div className="bg-white rounded-3xl md:rounded-[2.5rem] shadow-xl border border-gray-100 p-6 md:p-10 mt-6 md:mt-10 relative overflow-hidden">

      <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-50/30 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-50/30 rounded-full blur-3xl -ml-32 -mb-32 pointer-events-none"></div>

      {/* ── EDUCATIONAL QUALIFICATIONS ── */}
      <div className="relative mb-12">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8">
          <div className="flex items-center gap-4 md:gap-5">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100 text-emerald-600 flex items-center justify-center shadow-inner border border-emerald-200/50">
              <FaGraduationCap size={24} />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-500 to-blue-600 uppercase tracking-widest">Educational Qualifications</h2>
              <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mt-1 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Academic history & Credentials
              </p>
            </div>
          </div>
          <button onClick={() => setIsEduModalOpen(true)} className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 md:px-8 py-3 md:py-3.5 bg-emerald-600 text-white rounded-2xl text-[10px] font-bold uppercase tracking-widest hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-100 active:scale-95 group">
            <FaPlus size={12} className="group-hover:rotate-90 transition-transform" /> Add Qualification
          </button>
        </div>

        <div className="overflow-x-auto custom-scrollbar border border-gray-100 rounded-3xl shadow-sm bg-gray-50/30">
          <table className="min-w-[900px] w-full">
            <thead className="text-[10px] font-black text-white uppercase tracking-[0.2em] bg-gradient-to-r from-green-500 to-blue-600">
              <tr>
                <th className="py-5 px-8 text-left">Qualification</th>
                <th className="py-5 px-8 text-left">Institution</th>
                <th className="py-5 px-8 text-center">Period / Year</th>
                <th className="py-5 px-8 text-center">Location</th>
                <th className="py-5 px-8 text-center">Score (%)</th>
                <th className="py-5 px-8 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr><td colSpan="6" className="py-16 text-center text-[10px] font-bold text-gray-400 uppercase tracking-widest animate-pulse">Synchronizing Academy Data...</td></tr>
              ) : educationList.length > 0 ? (
                educationList.map((edu) => (
                  <tr key={edu._id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-4 md:py-5 px-4 md:px-8 font-bold text-gray-800 uppercase text-[11px] md:text-xs">{edu.qualification}</td>
                    <td className="py-4 md:py-5 px-4 md:px-8">
                      <div className="flex items-center gap-2">
                        <FaUniversity className="text-gray-300" />
                        <span className="text-[11px] md:text-xs font-bold text-gray-600 uppercase">{edu.institution}</span>
                      </div>
                    </td>
                    <td className="py-4 md:py-5 px-4 md:px-8 text-center">
                      <div className="text-[10px] font-bold text-gray-700">{edu.passingYear}</div>
                      {edu.startDate && <div className="text-[9px] font-bold text-gray-400 mt-1">{new Date(edu.startDate).getFullYear()} - {new Date(edu.endDate).getFullYear()}</div>}
                    </td>
                    <td className="py-4 md:py-5 px-4 md:px-8 text-center text-[10px] font-bold text-gray-500 uppercase">{edu.location}</td>
                    <td className="py-4 md:py-5 px-4 md:px-8 text-center">
                      <span className="px-3 py-1.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-xl border border-emerald-100">{edu.percentage}%</span>
                    </td>
                    <td className="py-4 md:py-5 px-4 md:px-8">
                      <div className="flex justify-center gap-2 md:gap-3">
                        <button onClick={() => handleEduEdit(edu)} className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all shadow-sm"><FaEdit size={12} /></button>
                        <button onClick={() => handleEduDelete(edu._id)} className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-all shadow-sm"><FaTrash size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="6" className="py-16 text-center text-[10px] font-bold text-gray-300 uppercase tracking-[0.2em] italic">Zero Academy Records Logged</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent my-16 opacity-50"></div>

      {/* ── PROFESSIONAL EXPERIENCE ── */}
      <div className="relative">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8">
          <div className="flex items-center gap-4 md:gap-5">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 text-blue-600 flex items-center justify-center shadow-inner border border-blue-200/50">
              <FaBriefcase size={24} />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-500 to-blue-600 uppercase tracking-widest">Professional Experience</h2>
              <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mt-1 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span> Career History & Documents
              </p>
            </div>
          </div>
          <button onClick={() => setIsExpModalOpen(true)} className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 md:px-8 py-3 md:py-3.5 bg-blue-600 text-white rounded-2xl text-[10px] font-bold uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg shadow-blue-100 active:scale-95 group">
            <FaPlus size={12} className="group-hover:rotate-90 transition-transform" /> Add Experience
          </button>
        </div>

        <div className="overflow-x-auto custom-scrollbar border border-gray-100 rounded-3xl shadow-sm bg-gray-50/30">
          <table className="min-w-[900px] w-full">
            <thead className="text-[10px] font-black text-white uppercase tracking-[0.2em] bg-gradient-to-r from-green-500 to-blue-600">
              <tr>
                <th className="py-5 px-8 text-left">Company & Role</th>
                <th className="py-5 px-8 text-left">Location</th>
                <th className="py-5 px-8 text-center">Tenure Period</th>
                <th className="py-5 px-8 text-center">Salary</th>
                <th className="py-5 px-8 text-center">Documents</th>
                <th className="py-5 px-8 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr><td colSpan="6" className="py-16 text-center text-[10px] font-bold text-gray-400 uppercase tracking-widest animate-pulse">Synchronizing Career Data...</td></tr>
              ) : experienceList.length > 0 ? (
                experienceList.map((exp) => (
                  <tr key={exp._id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-4 md:py-5 px-4 md:px-8">
                      <div className="text-[11px] md:text-xs font-bold text-gray-800 uppercase">{exp.role}</div>
                      <div className="text-[8px] md:text-[9px] font-bold text-blue-500 uppercase tracking-widest mt-1">{exp.companyName}</div>
                    </td>
                    <td className="py-4 md:py-5 px-4 md:px-8 text-[11px] md:text-xs font-bold text-gray-600 uppercase">{exp.location}</td>
                    <td className="py-4 md:py-5 px-4 md:px-8 text-center text-[9px] md:text-[10px] font-bold text-gray-700">{formatDate(exp.startDate)} — {formatDate(exp.endDate)}</td>
                    <td className="py-4 md:py-5 px-4 md:px-8 text-center">
                      <span className="px-3 py-1.5 bg-blue-50 text-blue-700 text-[10px] font-bold rounded-xl border border-blue-100">₹{exp.salary}</span>
                    </td>
                    <td className="py-4 md:py-5 px-4 md:px-8">
                      <div className="flex justify-center gap-2 md:gap-3">
                        {exp.offerLetter && <a href={renderDocumentUrl(exp.offerLetter)} target="_blank" rel="noreferrer" className="text-indigo-400 hover:text-indigo-600 transition-all" title="Offer Letter"><FaFilePdf size={16} /></a>}
                        {exp.payslip && <a href={renderDocumentUrl(exp.payslip)} target="_blank" rel="noreferrer" className="text-emerald-400 hover:text-emerald-600 transition-all" title="Payslip"><FaFilePdf size={16} /></a>}
                        {exp.relievingLetter && <a href={renderDocumentUrl(exp.relievingLetter)} target="_blank" rel="noreferrer" className="text-orange-400 hover:text-orange-600 transition-all" title="Relieving Letter"><FaFilePdf size={16} /></a>}
                        {exp.otherDocument && <a href={renderDocumentUrl(exp.otherDocument)} target="_blank" rel="noreferrer" className="text-purple-400 hover:text-purple-600 transition-all" title="Other Document"><FaFilePdf size={16} /></a>}
                        {!exp.offerLetter && !exp.payslip && !exp.relievingLetter && !exp.otherDocument && <span className="text-[9px] font-bold text-gray-300 uppercase">No Docs</span>}
                      </div>
                    </td>
                    <td className="py-4 md:py-5 px-4 md:px-8">
                      <div className="flex justify-center gap-2 md:gap-3">
                        <button onClick={() => handleExpEdit(exp)} className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all shadow-sm"><FaEdit size={12} /></button>
                        <button onClick={() => handleExpDelete(exp._id)} className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-all shadow-sm"><FaTrash size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="6" className="py-16 text-center text-[10px] font-bold text-gray-300 uppercase tracking-[0.2em] italic">Zero Career Records Logged</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── MODALS ── */}
      <AnimatePresence>
        {/* Education Modal */}
        {isEduModalOpen && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white w-full max-w-xl rounded-3xl md:rounded-[3rem] shadow-2xl p-6 md:p-12 border border-gray-100 relative max-h-[95vh] overflow-y-auto">
              <button className="absolute top-4 md:top-8 right-4 md:right-8 w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all shadow-sm border border-gray-100"
                onClick={() => { setIsEduModalOpen(false); setEditingEduId(null); setEduFormData({ qualification: "", institution: "", startDate: "", endDate: "", passingYear: "", location: "", percentage: "" }); }}>
                <FaTimes size={16} />
              </button>
              <div className="mb-8 md:mb-10 flex flex-col items-center text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-emerald-50 text-emerald-500 rounded-2xl md:rounded-[2rem] flex items-center justify-center mb-4 md:mb-5 shadow-inner border border-emerald-100"><FaGraduationCap size={30} /></div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 uppercase tracking-[0.1em]">{editingEduId ? "Update Academy" : "Log Academy"}</h3>
                <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-[0.3em] mt-2">Credentials Sync Protocol</p>
              </div>
              <form onSubmit={handleEduSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Qualification</label>
                  <input type="text" required value={eduFormData.qualification} onChange={e => setEduFormData({...eduFormData, qualification: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-sm font-bold outline-none" placeholder="e.g. B.Tech" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Institution</label>
                  <input type="text" required value={eduFormData.institution} onChange={e => setEduFormData({...eduFormData, institution: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-sm font-bold outline-none" placeholder="University Name" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Start Date</label>
                  <input type="date" value={eduFormData.startDate} onChange={e => setEduFormData({...eduFormData, startDate: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-[10px] font-bold outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">End Date</label>
                  <input type="date" value={eduFormData.endDate} onChange={e => setEduFormData({...eduFormData, endDate: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-[10px] font-bold outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Passing Year</label>
                  <input type="text" required value={eduFormData.passingYear} onChange={e => setEduFormData({...eduFormData, passingYear: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-sm font-bold outline-none" placeholder="2024" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Location</label>
                  <input type="text" value={eduFormData.location} onChange={e => setEduFormData({...eduFormData, location: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-sm font-bold outline-none" placeholder="City, State" />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Score (%)</label>
                  <input type="text" required value={eduFormData.percentage} onChange={e => setEduFormData({...eduFormData, percentage: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-sm font-bold outline-none" placeholder="85.5" />
                </div>
                <div className="md:col-span-2 pt-6">
                  <button type="submit" disabled={isSubmitting} className="w-full py-5 bg-gradient-to-r from-emerald-600 to-teal-700 text-white rounded-2xl text-[11px] font-bold uppercase tracking-[0.3em] shadow-xl shadow-emerald-100 hover:scale-[1.01] transition-all active:scale-95 disabled:opacity-50">
                    {isSubmitting ? "Transmitting..." : editingEduId ? "Update Records" : "Save Records"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* Experience Modal */}
        {isExpModalOpen && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white w-full max-w-2xl rounded-3xl md:rounded-[3rem] shadow-2xl p-6 md:p-12 border border-gray-100 relative max-h-[90vh] overflow-y-auto custom-scrollbar">
              <button className="absolute top-4 md:top-8 right-4 md:right-8 w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all shadow-sm border border-gray-100"
                onClick={() => { setIsExpModalOpen(false); setEditingExpId(null); setExpFormData({ companyName: '', role: '', location: '', salary: '', startDate: '', endDate: '', offerLetter: null, payslip: null, relievingLetter: null, otherDocument: null }); }}>
                <FaTimes size={16} />
              </button>
              <div className="mb-8 md:mb-10 flex flex-col items-center text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-blue-50 text-blue-500 rounded-2xl md:rounded-[2rem] flex items-center justify-center mb-4 md:mb-5 shadow-inner border border-blue-100"><FaBriefcase size={30} /></div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 uppercase tracking-[0.1em]">{editingExpId ? "Update Career" : "Log Career"}</h3>
                <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-[0.3em] mt-2">Professional Sync Protocol</p>
              </div>
              <form onSubmit={handleExpSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Company Name</label>
                  <input type="text" required value={expFormData.companyName} onChange={e => setExpFormData({...expFormData, companyName: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold outline-none" placeholder="Acme Corp" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Role</label>
                  <input type="text" required value={expFormData.role} onChange={e => setExpFormData({...expFormData, role: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold outline-none" placeholder="Sr. Engineer" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Location</label>
                  <input type="text" required value={expFormData.location} onChange={e => setExpFormData({...expFormData, location: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold outline-none" placeholder="City, Country" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Salary</label>
                  <input type="text" required value={expFormData.salary} onChange={e => setExpFormData({...expFormData, salary: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold outline-none" placeholder="e.g. 50,000" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Start Date</label>
                  <input type="date" required value={expFormData.startDate} onChange={e => setExpFormData({...expFormData, startDate: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-[10px] font-bold outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">End Date (Current)</label>
                  <input type="date" value={expFormData.endDate} onChange={e => setExpFormData({...expFormData, endDate: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-[10px] font-bold outline-none" />
                </div>

                <div className="md:col-span-2 grid grid-cols-2 gap-4 mt-4">
                  <div className="p-5 bg-indigo-50/50 border-2 border-dashed border-indigo-200 rounded-3xl flex flex-col items-center gap-2 cursor-pointer hover:bg-indigo-100 transition relative">
                    <FaUpload className="text-indigo-500" />
                    <span className="text-[9px] font-bold text-indigo-700 uppercase tracking-widest text-center">{expFormData.offerLetter ? expFormData.offerLetter.name : "OFFER LETTER"}</span>
                    <input type="file" onChange={e => setExpFormData({...expFormData, offerLetter: e.target.files[0]})} className="absolute inset-0 opacity-0 cursor-pointer" />
                  </div>
                  <div className="p-5 bg-emerald-50/50 border-2 border-dashed border-emerald-200 rounded-3xl flex flex-col items-center gap-2 cursor-pointer hover:bg-emerald-100 transition relative">
                    <FaUpload className="text-emerald-500" />
                    <span className="text-[9px] font-bold text-emerald-700 uppercase tracking-widest text-center">{expFormData.payslip ? expFormData.payslip.name : "PAYSLIP"}</span>
                    <input type="file" onChange={e => setExpFormData({...expFormData, payslip: e.target.files[0]})} className="absolute inset-0 opacity-0 cursor-pointer" />
                  </div>
                  <div className="p-5 bg-orange-50/50 border-2 border-dashed border-orange-200 rounded-3xl flex flex-col items-center gap-2 cursor-pointer hover:bg-orange-100 transition relative">
                    <FaUpload className="text-orange-500" />
                    <span className="text-[9px] font-bold text-orange-700 uppercase tracking-widest text-center">{expFormData.relievingLetter ? expFormData.relievingLetter.name : "RELIEVING LETTER"}</span>
                    <input type="file" onChange={e => setExpFormData({...expFormData, relievingLetter: e.target.files[0]})} className="absolute inset-0 opacity-0 cursor-pointer" />
                  </div>
                  <div className="p-5 bg-purple-50/50 border-2 border-dashed border-purple-200 rounded-3xl flex flex-col items-center gap-2 cursor-pointer hover:bg-purple-100 transition relative">
                    <FaUpload className="text-purple-500" />
                    <span className="text-[9px] font-bold text-purple-700 uppercase tracking-widest text-center">{expFormData.otherDocument ? expFormData.otherDocument.name : "OTHER DOCUMENT"}</span>
                    <input type="file" onChange={e => setExpFormData({...expFormData, otherDocument: e.target.files[0]})} className="absolute inset-0 opacity-0 cursor-pointer" />
                  </div>
                </div>

                <div className="md:col-span-2 pt-6">
                  <button type="submit" disabled={isSubmitting} className="w-full py-5 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-2xl text-[11px] font-bold uppercase tracking-[0.3em] shadow-xl shadow-blue-100 hover:scale-[1.01] transition-all active:scale-95 disabled:opacity-50">
                    {isSubmitting ? "Transmitting..." : editingExpId ? "Update Career" : "Authorize Log"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="flex items-center justify-center gap-3 text-[10px] font-bold text-gray-400 uppercase tracking-[0.3em] mt-10">
        <FaInfoCircle className="text-indigo-400" />
        Synchronized Professional & Academic Records Protocol • {new Date().getFullYear()}
      </div>
    </div>
  );
};

export default CandidateEducation;